import { useMemo, useState, useRef, useCallback, useEffect } from "react";
import dagre from "@dagrejs/dagre";
import {
  STATUS_ICON,
  GRAPH_STATUS_CFG as STATUS_CFG,
  GRAPH_PHASE_NAMES as PHASE_NAMES,
  GRAPH_PHASE_COLOR as PHASE_HUE,
} from "../api/constants/status";
import { formatDuration } from "../api/utils/format";
import {
  useTaskNodes,
  useExecByTask,
  useUnblockedTaskIds,
} from "../api/context/ProjectDashboardContext";
import type { TaskVM, ExecutionEntryVM } from "../api/types/project";

const NODE_W = 96;
const NODE_H = 45;
const ZOOM_STEP = 0.15;
const MIN_ZOOM = 0.3;
const MAX_ZOOM = 4;

interface LayoutNode {
  taskId: string;
  subject: string;
  status: string;
  phase: number;
  priority: string;
  humanGate: boolean;
  blockedBy: string[];
  x: number;
  y: number;
}

interface LayoutEdge {
  from: string;
  to: string;
  points: { x: number; y: number }[];
}

type ColorMode = "status" | "duration";

/** Map normalized 0-1 value to green→yellow→red via HSL (hue 120→60→0). */
function heatColor(t: number): string {
  const clamped = Math.max(0, Math.min(1, t));
  const hue = 120 - clamped * 120; // 120=green, 60=yellow, 0=red
  return `hsl(${hue}, 80%, 35%)`;
}

export function GraphView({ hideComplete }: { hideComplete: boolean }) {
  // Data from ProjectDashboardContext — no direct useQuery calls
  const tasksState = useTaskNodes();
  const execByTaskState = useExecByTask();
  const unblockedState = useUnblockedTaskIds();

  const tasks: TaskVM[] | undefined =
    tasksState.status === "ready" ? tasksState.data : undefined;
  const execByTask: Record<string, ExecutionEntryVM> =
    execByTaskState.status === "ready" ? execByTaskState.data : {};
  const readyIds: Set<string> =
    unblockedState.status === "ready" ? unblockedState.data : new Set<string>();
  const [hovered, setHovered] = useState<string | null>(null);
  const [colorMode, setColorMode] = useState<ColorMode>("status");
  const containerRef = useRef<HTMLDivElement>(null);
  const zoomRef = useRef(1);

  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 });

  // Keep ref in sync for the native listener
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);

  const clamp = (z: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));

  // Use only the + / - buttons for zoom. No scroll zoom at all — scroll passes through to page.
  // This avoids the ctrl+scroll conflict with browser zoom entirely.

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    setDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
  }, [pan]);

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragging) return;
    setPan({
      x: dragStart.current.panX + (e.clientX - dragStart.current.x) / zoomRef.current,
      y: dragStart.current.panY + (e.clientY - dragStart.current.y) / zoomRef.current,
    });
  }, [dragging]);

  const onMouseUp = useCallback(() => setDragging(false), []);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length !== 1) return;
    const t = e.touches[0];
    setDragging(true);
    dragStart.current = { x: t.clientX, y: t.clientY, panX: pan.x, panY: pan.y };
  }, [pan]);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!dragging || e.touches.length !== 1) return;
    const t = e.touches[0];
    setPan({
      x: dragStart.current.panX + (t.clientX - dragStart.current.x) / zoomRef.current,
      y: dragStart.current.panY + (t.clientY - dragStart.current.y) / zoomRef.current,
    });
  }, [dragging]);

  const onTouchEnd = useCallback(() => setDragging(false), []);

  const { nodes, edges, graphW, graphH } = useMemo(() => {
    if (!tasks) return { nodes: [], edges: [], graphW: 0, graphH: 0 };
    const filtered = hideComplete ? tasks.filter((t) => t.status !== "complete" && t.status !== "deferred") : tasks;
    if (filtered.length === 0) return { nodes: [], edges: [], graphW: 0, graphH: 0 };

    const visibleIds = new Set(filtered.map((t) => t.taskId));
    const g = new dagre.graphlib.Graph();
    g.setGraph({ rankdir: "TB", nodesep: 14, ranksep: 40, edgesep: 8, marginx: 20, marginy: 20 });
    g.setDefaultEdgeLabel(() => ({}));

    for (const t of filtered) g.setNode(t.taskId, { width: NODE_W, height: NODE_H });
    for (const t of filtered) for (const dep of t.blockedBy) if (visibleIds.has(dep)) g.setEdge(dep, t.taskId);
    dagre.layout(g);

    const taskMap = new Map(filtered.map((t) => [t.taskId, t]));
    const layoutNodes: LayoutNode[] = g.nodes().map((id) => {
      const pos = g.node(id);
      const t = taskMap.get(id)!;
      return {
        taskId: id, subject: t.subject, status: t.status, phase: t.phase,
        priority: t.priority, humanGate: t.humanGate, blockedBy: t.blockedBy,
        x: pos.x - NODE_W / 2, y: pos.y - NODE_H / 2,
      };
    });

    const layoutEdges: LayoutEdge[] = g.edges().map((e) => ({
      from: e.v, to: e.w, points: g.edge(e).points ?? [],
    }));

    const gl = g.graph();
    return { nodes: layoutNodes, edges: layoutEdges, graphW: gl.width ?? 600, graphH: gl.height ?? 400 };
  }, [tasks, hideComplete]);

  const fitToView = useCallback(() => {
    setPan({ x: 0, y: 0 });
    if (containerRef.current && graphW > 0 && graphH > 0) {
      const cw = containerRef.current.clientWidth;
      const fitZoom = clamp(cw / graphW);
      setZoom(fitZoom);
    } else {
      setZoom(1);
    }
  }, [graphW, graphH]);

  // Auto-fit when data changes or on first render
  useEffect(() => { fitToView(); }, [hideComplete, fitToView]);

  // Hover highlighting — full upstream/downstream chain
  const hlNodes = new Set<string>();
  const hlEdges = new Set<number>();
  if (hovered) {
    hlNodes.add(hovered);
    const queue = [hovered];
    const visited = new Set([hovered]);
    while (queue.length) {
      const id = queue.shift()!;
      edges.forEach((e, i) => {
        if (e.to === id && !visited.has(e.from)) {
          visited.add(e.from);
          hlNodes.add(e.from);
          hlEdges.add(i);
          queue.push(e.from);
        }
      });
    }
    const queue2 = [hovered];
    const visited2 = new Set([hovered]);
    while (queue2.length) {
      const id = queue2.shift()!;
      edges.forEach((e, i) => {
        if (e.from === id && !visited2.has(e.to)) {
          visited2.add(e.to);
          hlNodes.add(e.to);
          hlEdges.add(i);
          queue2.push(e.to);
        }
      });
    }
  }

  function edgePath(points: { x: number; y: number }[]): string {
    if (points.length < 2) return "";
    const [first, ...rest] = points;
    let d = `M ${first.x} ${first.y}`;
    for (let i = 0; i < rest.length - 1; i++) {
      const c = rest[i], n = rest[i + 1];
      d += ` Q ${c.x} ${c.y}, ${(c.x + n.x) / 2} ${(c.y + n.y) / 2}`;
    }
    d += ` L ${rest[rest.length - 1].x} ${rest[rest.length - 1].y}`;
    return d;
  }

  // Compute heatmap color map: taskId → fill color for duration mode
  const heatmapColors = useMemo(() => {
    const colors = new Map<string, string>();
    if (colorMode === "status") return colors;

    const values = new Map<string, number>();
    for (const [taskId, exec] of Object.entries(execByTask)) {
      if (exec.durationMs != null && exec.durationMs > 0) {
        values.set(taskId, exec.durationMs);
      }
    }

    if (values.size === 0) return colors;

    const vals = Array.from(values.values());
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const range = max - min;

    values.forEach((val, taskId) => {
      const normalized = range > 0 ? (val - min) / range : 0;
      colors.set(taskId, heatColor(normalized));
    });
    return colors;
  }, [colorMode, execByTask]);

  const hoveredNode = nodes.find((n) => n.taskId === hovered);
  const hoveredExec = hovered ? execByTask[hovered] : undefined;
  const phases = [...new Set(nodes.map((n) => n.phase))].sort((a, b) => a - b);

  if (!tasks) return <div className="animate-pulse h-[70vh] bg-zinc-900/60 rounded-lg" />;

  return (
    <div className="flex flex-col gap-2">
      {/* toolbar */}
      <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
        <div className="flex items-center border border-zinc-700 rounded overflow-hidden">
          <button onClick={() => setZoom((z) => clamp(z - ZOOM_STEP))} className="px-2 py-1 text-zinc-400 hover:bg-zinc-800">&minus;</button>
          <span className="px-1.5 py-1 text-zinc-500 tabular-nums bg-zinc-900 min-w-[36px] text-center">{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom((z) => clamp(z + ZOOM_STEP))} className="px-2 py-1 text-zinc-400 hover:bg-zinc-800">+</button>
        </div>

        <button onClick={fitToView} className="px-2 py-1 rounded border bg-zinc-900 border-zinc-700 text-zinc-400">Fit</button>

        <select
          value={colorMode}
          onChange={(e) => setColorMode(e.target.value as ColorMode)}
          className="px-2 py-1 rounded border bg-zinc-900 border-zinc-700 text-zinc-400 text-[11px] appearance-none cursor-pointer"
        >
          <option value="status">Color: Status</option>
          <option value="duration">Color: Duration</option>
        </select>
      </div>

      {nodes.length === 0 ? (
        <div className="text-zinc-600 text-center py-16 border border-zinc-800 rounded-lg">No tasks</div>
      ) : (
        <div className="relative">
          <div
            ref={containerRef}
            className="rounded-lg border border-zinc-800 overflow-hidden"
            style={{
              cursor: dragging ? "grabbing" : "grab",
              background: "#09090b",
              touchAction: "none",
            }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <svg
              width="100%"
              viewBox={`${-pan.x} ${-pan.y} ${graphW / zoom} ${graphH / zoom}`}
              preserveAspectRatio="xMidYMid meet"
              style={{ display: "block", height: Math.max(graphH * zoom, 200) }}
            >
              <g>
                <defs>
                  <marker id="arr" viewBox="0 0 8 6" refX="8" refY="3" markerWidth="6" markerHeight="5" orient="auto">
                    <path d="M0 0 L8 3 L0 6z" fill="#3f3f46" />
                  </marker>
                  <marker id="arr-hl" viewBox="0 0 8 6" refX="8" refY="3" markerWidth="6" markerHeight="5" orient="auto">
                    <path d="M0 0 L8 3 L0 6z" fill="#818cf8" />
                  </marker>
                </defs>

                {/* edges */}
                {edges.map((e, i) => {
                  const hl = hlEdges.has(i);
                  const dim = hovered && !hl;
                  return (
                    <path
                      key={`${e.from}-${e.to}`}
                      d={edgePath(e.points)}
                      fill="none"
                      stroke={hl ? "#818cf8" : "#27272a"}
                      strokeWidth={hl ? 2 : 1}
                      strokeOpacity={dim ? 0.06 : 1}
                      markerEnd={hl ? "url(#arr-hl)" : "url(#arr)"}
                    />
                  );
                })}

                {/* nodes */}
                {nodes.map((node) => {
                  const isHl = hlNodes.has(node.taskId);
                  const dim = hovered && !isHl;
                  const effectiveStatus = (node.status === "pending" || node.status === "waiting") && readyIds.has(node.taskId) ? "ready" : node.status;
                  const cfg = STATUS_CFG[effectiveStatus] ?? STATUS_CFG.pending;
                  const phaseColor = PHASE_HUE[node.phase] ?? "#71717a";

                  return (
                    <g
                      key={node.taskId}
                      onMouseEnter={() => setHovered(node.taskId)}
                      onMouseLeave={() => setHovered(null)}
                      style={{ cursor: "pointer", opacity: dim ? 0.1 : 1, transition: "opacity 0.15s" }}
                    >
                      {/* bg */}
                      <rect
                        x={node.x} y={node.y}
                        width={NODE_W} height={NODE_H}
                        rx={4}
                        fill={colorMode !== "status" && heatmapColors.has(node.taskId) ? heatmapColors.get(node.taskId)! : cfg.bg}
                        stroke={isHl ? "#818cf8" : cfg.border}
                        strokeWidth={isHl ? 1.5 : 0.5}
                      />


                      {/* status icon + task id */}
                      {(() => {
                        const icon = STATUS_ICON[effectiveStatus];
                        if (icon) {
                          return (
                            <>
                              <text
                                x={node.x + 12} y={node.y + NODE_H / 2}
                                textAnchor="middle" dy="0.35em"
                                fontSize={11} fontFamily="sans-serif"
                                fill={cfg.fg}
                              >
                                {icon}
                                {effectiveStatus === "in_progress" && (
                                  <animate attributeName="opacity" values="1;0.3;1" dur="1.5s" repeatCount="indefinite" />
                                )}
                              </text>
                              <text
                                x={node.x + NODE_W / 2 + 6} y={node.y + NODE_H / 2}
                                textAnchor="middle" dy="0.35em"
                                fontSize={16} fontWeight={700} fontFamily="monospace"
                                fill={cfg.fg}
                              >
                                {node.taskId}
                              </text>
                            </>
                          );
                        }
                        return (
                          <text
                            x={node.x + NODE_W / 2} y={node.y + NODE_H / 2}
                            textAnchor="middle" dy="0.35em"
                            fontSize={16} fontWeight={700} fontFamily="monospace"
                            fill={cfg.fg}
                          >
                            {node.taskId}
                          </text>
                        );
                      })()}

                      {/* human gate dot */}
                      {node.humanGate && (
                        <circle
                          cx={node.x + NODE_W - 4} cy={node.y + 4} r={3}
                          fill="#fb923c" stroke="#431407" strokeWidth={0.5}
                        />
                      )}
                    </g>
                  );
                })}
              </g>
            </svg>
          </div>

          {/* tooltip */}
          {hoveredNode && (
            <div className="absolute top-2 left-2 sm:left-auto sm:right-2 pointer-events-none z-50 w-56 max-w-[calc(100%-16px)]">
              <div className="bg-zinc-900/95 backdrop-blur border border-zinc-700 rounded-lg p-2.5 shadow-xl space-y-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold font-mono text-white">{hoveredNode.taskId}</span>
                  {(() => {
                    const hs = (hoveredNode.status === "pending" || hoveredNode.status === "waiting") && readyIds.has(hoveredNode.taskId) ? "ready" : hoveredNode.status;
                    const hcfg = STATUS_CFG[hs] ?? STATUS_CFG.pending;
                    return (
                      <span
                        className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                        style={{ color: hcfg.fg, backgroundColor: hcfg.fg + "22" }}
                      >
                        {hcfg.label}
                      </span>
                    );
                  })()}
                  <span className="text-[10px] font-mono text-zinc-500 ml-auto">{hoveredNode.priority}</span>
                </div>
                <p className="text-[11px] text-zinc-300 leading-snug">{hoveredNode.subject}</p>
                <div className="flex gap-2 text-[10px] text-zinc-500">
                  <span style={{ color: PHASE_HUE[hoveredNode.phase] }}>{PHASE_NAMES[hoveredNode.phase]}</span>
                  {hoveredNode.humanGate && <span className="text-orange-400">Human Gate</span>}
                </div>
                {hoveredNode.blockedBy.length > 0 && (
                  <div className="text-[10px] text-zinc-500">
                    Depends on: <span className="text-zinc-400 font-mono">{hoveredNode.blockedBy.join(", ")}</span>
                  </div>
                )}
                {/* Execution details shown in heatmap modes or when execution data exists */}
                {hoveredExec && colorMode !== "status" && (
                  <div className="border-t border-zinc-700/50 pt-1.5 mt-1 space-y-0.5">
                    {hoveredExec.durationMs != null && (
                      <div className="text-[10px] text-zinc-400">
                        Duration: <span className="text-zinc-300 font-mono">{formatDuration(hoveredExec.durationMs)}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
